import { useState, useCallback, useRef, useEffect } from "react";

interface UseBrowserSTTOptions {
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
  onResult?: (text: string, isFinal: boolean) => void;
  onError?: (error: Error) => void;
  onEnd?: () => void;
}

interface UseBrowserSTTReturn {
  start: () => void;
  stop: () => void;
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  interimTranscript: string;
  error: Error | null;
  reset: () => void;
}

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export const useBrowserSTT = (options: UseBrowserSTTOptions = {}): UseBrowserSTTReturn => {
  const {
    language = "ru-RU",
    continuous = false,
    interimResults = true,
    onResult,
    onError,
    onEnd,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isStartedRef = useRef(false);

  // Check browser support with actual instance creation (works in iframes)
  useEffect(() => {
    if (typeof window === "undefined") {
      setIsSupported(false);
      return;
    }

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      console.warn("[Browser STT] Speech recognition API not available");
      setIsSupported(false);
      return;
    }

    // Try to create an instance to verify it actually works
    try {
      const testRecognition = new SpeechRecognitionAPI();
      testRecognition.abort();
      setIsSupported(true);
      console.log("[Browser STT] Speech recognition supported and functional");
    } catch (e) {
      console.warn("[Browser STT] Feature blocked:", e);
      setIsSupported(false);
    }
  }, []);

  // Initialize recognition
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionAPI();

    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log("[Browser STT] Started listening");
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";
      let interim = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;

        if (result.isFinal) {
          finalTranscript += text;
        } else {
          interim += text;
        }
      }

      if (finalTranscript) {
        setTranscript((prev) => prev + finalTranscript);
        onResult?.(finalTranscript, true);
        console.log("[Browser STT] Final:", finalTranscript);
      }

      setInterimTranscript(interim);
      if (interim) {
        onResult?.(interim, false);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error("[Browser STT] Error:", event.error, event.message);
      
      if (event.error === "aborted") return;

      // User-friendly error messages
      let errorMessage = "Ошибка распознавания речи";
      switch (event.error) {
        case "not-allowed":
          errorMessage = "Доступ к микрофону заблокирован. Разрешите доступ в настройках браузера.";
          break;
        case "no-speech":
          errorMessage = "Речь не обнаружена. Попробуйте ещё раз.";
          break;
        case "network":
          errorMessage = "Ошибка сети. Проверьте интернет-соединение.";
          break;
        case "audio-capture":
          errorMessage = "Микрофон не найден. Проверьте подключение.";
          break;
        case "service-not-allowed":
          errorMessage = "Сервис распознавания недоступен в этом браузере.";
          break;
      }

      const err = new Error(errorMessage);
      setError(err);
      onError?.(err);
      setIsListening(false);
      isStartedRef.current = false;
    };

    recognition.onend = () => {
      console.log("[Browser STT] Ended");
      setIsListening(false);
      isStartedRef.current = false;
      setInterimTranscript("");
      onEnd?.();

      // Auto-restart if continuous mode
      if (continuous && isStartedRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.error("[Browser STT] Restart failed:", e);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore abort errors
        }
      }
    };
  }, [language, continuous, interimResults, onResult, onError, onEnd, isSupported]);

  const start = useCallback(() => {
    if (!isSupported) {
      const err = new Error("Speech recognition not supported");
      setError(err);
      onError?.(err);
      return;
    }

    if (isStartedRef.current) {
      console.log("[Browser STT] Already listening");
      return;
    }

    setTranscript("");
    setInterimTranscript("");
    setError(null);

    try {
      isStartedRef.current = true;
      recognitionRef.current?.start();
    } catch (e) {
      console.error("[Browser STT] Start failed:", e);
      isStartedRef.current = false;
      const err = e instanceof Error ? e : new Error("Failed to start speech recognition");
      setError(err);
      onError?.(err);
    }
  }, [isSupported, onError]);

  const stop = useCallback(() => {
    if (!recognitionRef.current) return;

    isStartedRef.current = false;
    try {
      recognitionRef.current.stop();
    } catch (e) {
      console.error("[Browser STT] Stop failed:", e);
    }
  }, []);

  const reset = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    setError(null);
  }, []);

  return {
    start,
    stop,
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    error,
    reset,
  };
};
